import { useCallback } from "react";
import { useLocation, useSearch } from "wouter";

/**
 * Canonical hook for tab↔URL sync across PadPulse's tabbed pages.
 *
 * Pattern: read `?<key>=` from the URL on every render (reactive via wouter's
 * useSearch), write back on tab change via navigate(...) (push by default) so
 * browser back/forward naturally moves between previously-visited tabs.
 *
 * Pass `replace: true` only for same-view refinements (e.g. a secondary
 * sub-tab that shouldn't add a history entry on its own).
 *
 * Pass `tabs` to validate URL values against a known allow-list. Any value not
 * in the list silently falls back to `defaultTab`, preventing blank content
 * when someone manually edits the URL or follows a stale bookmark.
 *
 * IMPORTANT (Task #2042): always pass `tabs` for any page that consumes
 * `?tab=`. Sidebar navigation links navigate to plain paths (no query
 * string), but a `?tab=<value>` set on one page can still survive a
 * browser-back / bookmarked-link / hand-typed-URL trip into another page
 * that happens to read the same `tab` key. The allow-list is what keeps
 * those stale values from silently activating an unrelated tab — or
 * leaving the page on a blank tab panel because the value matches none of
 * the on-page tabs. New tab pages MUST supply `tabs`; existing ones are
 * audited in `client/src/__tests__/tab-url-sync.test.tsx`.
 *
 * Usage:
 *   const [activeTab, setActiveTab] = useTabQueryParam("tab", "overview", {
 *     tabs: ["overview", "models", "suggestions"],
 *   });
 *
 * For pages with multiple independent tab groups use distinct keys:
 *   const [section, setSection] = useTabQueryParam("tab", "reviews");
 *   const [subTab, setSubTab]   = useTabQueryParam("reviewType", "all");
 */
export function useTabQueryParam(
  key: string,
  defaultTab: string,
  options: { replace?: boolean; tabs?: readonly string[] } = {},
): [string, (tab: string) => void] {
  const [pathname, navigate] = useLocation();
  const search = useSearch();
  const replace = options.replace ?? false;

  const params = new URLSearchParams(search);
  const raw = params.get(key) || defaultTab;
  // Clamp to the allow-list when one is provided; fall back to defaultTab for
  // any value not in the list (mangled bookmarks, copy-paste errors, etc.)
  const activeTab =
    options.tabs && options.tabs.length > 0
      ? options.tabs.includes(raw)
        ? raw
        : defaultTab
      : raw;

  const setActiveTab = useCallback(
    (tab: string) => {
      const next = new URLSearchParams(search);
      if (tab === defaultTab) {
        next.delete(key);
      } else {
        next.set(key, tab);
      }
      const qs = next.toString();
      navigate(qs ? `${pathname}?${qs}` : pathname, { replace });
    },
    [key, defaultTab, pathname, search, navigate, replace],
  );

  return [activeTab, setActiveTab];
}
